import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogContent, DialogTitle, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import {
  AXIS_TICK, BORDER, ChartCard, ChartSkeleton, NoData, SeriesLegend, ShareBar, Stat, StatStrip, SubText, TABLE_SX, TipBox, TipRow, TooltipInjected, tooltipPoint
} from '../../OnlineJournal/components/analytics/chartKit';
import { CHART } from '../../OnlineJournal/constants';
import { deltaPct, fmtDay, fmtDelta, fmtInt, fmtPct, share } from '../../OnlineJournal/format';
import { useLoad } from '../../OnlineJournal/hooks/useLoad';
import { remainsApi } from '../../Remains/remainsApi';
import { GROUP_COLORS, WATCH_COLORS } from '../constants';
import { plural } from '../format';
import { DocumentFilters, ProductOption } from '../types';
import { analyticsApi } from './analyticsApi';
import { bucketRange, bucketTitle, diffDays, dm, fmtHours, fmtPack, KIND_COLORS, KIND_LABELS, kindPatch } from './analyticsFormat';
import { ByToggle, HEIGHT, useBucketBy } from './analyticsKit';
import { BucketBy, Period, ProductCardResponse, ProductSummary } from './analyticsTypes';

type Kind = 'direct' | 'via' | 'return';
type Point = ProductCardResponse['timeline'][number];

const KINDS: Kind[] = ['direct', 'via', 'return'];
const LEGEND = KINDS.map(k => ({ key: k, label: KIND_LABELS[k], color: KIND_COLORS[k] }));
const PAGE = 20;
const REMAINS_TOP = 15;

interface Props {
  id: string;
  params: Record<string, string>;
  paramsKey: string;
  period: Period;
  reloadToken: number;
  onClose: () => void;
  /** Открыть карточку другого товара (аналог) */
  onOpen: (id: string) => void;
  onCohort: (extra: Partial<DocumentFilters>, range?: Period) => void;
}

const trendHint = (cur: number, prev: number): string => {
  const d = deltaPct(cur, prev);
  return d === null ? 'в прошлом периоде не было' : `${fmtDelta(d)} · было ${fmtPack(prev)}`;
};

function summaryStats(data: ProductCardResponse): Stat[] {
  const { cur, prev } = data.summary;
  const lead = data.lead;
  const leadDelta = lead.product.p50Hours !== null && lead.others.p50Hours ? lead.product.p50Hours / lead.others.p50Hours : null;
  const failed = (s: ProductSummary) => s.errorDocs + s.cleanedDocs;
  return [
    { label: 'Упаковок', value: fmtPack(cur.qty), hint: trendHint(cur.qty, prev.qty) },
    { label: 'Документов', value: fmtInt(cur.docs), hint: trendHint(cur.docs, prev.docs) },
    { label: 'Строк', value: fmtInt(cur.lines), hint: `в среднем ${fmtPack(cur.avgQty)} уп. на строку` },
    { label: 'Отправители / получатели', value: `${fmtInt(cur.senders)} / ${fmtInt(cur.recipients)}`, hint: 'аптеки и склады' },
    { label: 'Возвраты', value: fmtPct(cur.returnShare), color: KIND_COLORS.return, hint: `${fmtPack(cur.returnQty)} уп. · было ${fmtPct(prev.returnShare)}` },
    { label: 'Через РЦ', value: fmtPct(cur.viaShare), color: KIND_COLORS.via, hint: `${fmtPack(cur.viaQty)} уп. · было ${fmtPct(prev.viaShare)}` },
    {
      label: 'Ошибки', value: fmtPct(cur.errorShare), color: cur.errorDocs ? GROUP_COLORS.error : undefined,
      hint: `${fmtInt(cur.errorDocs)} док.${cur.rejectedDocs ? `, отклонено ${fmtInt(cur.rejectedDocs)}` : ''}`,
      tooltip: 'Документы с товаром, которые сейчас в статусах ошибки'
    },
    {
      label: 'Автоочистка', value: fmtInt(cur.cleanedDocs), color: cur.cleanedDocs ? WATCH_COLORS.warn : undefined,
      hint: `всего проблемных ${fmtInt(failed(cur))} · было ${fmtInt(failed(prev))}`, tooltip: 'Документы с товаром, отменённые или скрытые ночной автоочисткой'
    },
    {
      label: 'Нехватка остатка', value: fmtInt(cur.shortLines), color: cur.shortLines ? WATCH_COLORS.warn : undefined,
      hint: cur.shortLines ? `не хватило ${fmtPack(cur.shortQty)} уп.` : 'не было', tooltip: 'Строки с ответом 1С «Не достаточно остатка»'
    },
    { label: 'Маркировка', value: fmtPct(cur.markedShare), hint: `${fmtInt(cur.markedLines)} строк с кодами` },
    { label: 'Строки по 1 уп.', value: fmtPct(cur.singleShare), hint: `${fmtInt(cur.singleLines)} строк` },
    {
      label: 'Медиана до «Завершен»', value: fmtHours(lead.product.p50Hours),
      color: leadDelta !== null && leadDelta > 1.2 ? WATCH_COLORS.warn : undefined,
      hint: `у остальных ${fmtHours(lead.others.p50Hours)}`,
      tooltip: `Документы с товаром: завершено ${fmtInt(lead.product.completed)}, p90 ${fmtHours(lead.product.p90Hours)}; остальные документы периода: p90 ${fmtHours(lead.others.p90Hours)}`
    }
  ];
}

const Tip: React.FC<TooltipInjected<Point> & { by?: BucketBy }> = props => {
  const p = tooltipPoint(props);
  if (!p) return null;
  const total = p.direct + p.via + p.return;
  const rows: TipRow[] = [
    { label: 'Упаковок', value: fmtPack(total), bold: true },
    ...KINDS.map(k => ({ label: KIND_LABELS[k], value: fmtPack(p[k]), color: KIND_COLORS[k], hint: total ? fmtPct(share(p[k], total)) : undefined })),
    { label: 'Документов', value: fmtInt(p.docs), divider: true }
  ];
  return <TipBox width={260} title={bucketTitle(p.date, props.by || 'day', p.partial)} rows={rows} footer={p.docs ? 'Клик по столбцу - документы с товаром этого вида' : undefined} />;
};

const SignalBlock: React.FC<{ title: string; text: string; color?: string; children?: React.ReactNode }> = ({ title, text, color, children }) => (
  <Box sx={{ py: 1, borderBottom: '1px solid #f1f5f9', '&:last-of-type': { borderBottom: 'none' } }}>
    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: color || '#0f172a' }}>{title}</Typography>
    <SubText>{text}</SubText>
    {children && <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>{children}</Box>}
  </Box>
);

const SignalRow: React.FC<{ name: string; value: string; onClick?: () => void }> = ({ name, value, onClick }) => (
  <Box
    component={onClick ? 'button' : 'div'} type={onClick ? 'button' : undefined} onClick={onClick}
    sx={{
      display: 'flex', gap: 1, alignItems: 'center', border: 'none', bgcolor: 'transparent', font: 'inherit', textAlign: 'left', px: 0.5, py: 0.25,
      borderRadius: 1, cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { bgcolor: '#f8fafc' } : undefined
    }}
  >
    <Typography sx={{ fontSize: '0.75rem', flexGrow: 1, minWidth: 0 }} noWrap title={name}>{name}</Typography>
    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{value}</Typography>
  </Box>
);

/** Остатки товара сейчас (не за период) - отдельным запросом справочника остатков, без записи в журнал */
const RemainsCard: React.FC<{ id: string; sku: string; reloadToken: number }> = ({ id, sku, reloadToken }) => {
  const [shown, setShown] = useState(REMAINS_TOP);
  const load = useLoad(
    (_silent: boolean, signal: AbortSignal) => remainsApi.search({ term: sku, mode: 'sku', minQty: 0 }, true, signal),
    `remains|${sku}`, reloadToken, 'Не удалось загрузить остатки'
  );
  const rows = useMemo(() => {
    if (!load.data) return [];
    const names = new Map(load.data.pharmacies.map(p => [p.guid, p]));
    return load.data.stocks
      .filter(s => s.zid.toLowerCase() === id && s.qty > 0)
      .map(s => ({ stock: s, pharmacy: names.get(s.guid) }))
      .sort((a, b) => b.stock.qty - a.stock.qty);
  }, [load.data, id]);
  const total = rows.reduce((a, r) => a + r.stock.qty, 0);
  const max = rows.length ? rows[0].stock.qty : 0;
  const expiring = rows.filter(r => r.stock.expiry && r.stock.expiry.nearest && r.stock.expiry.nearest.daysLeft <= 90).length;

  return (
    <ChartCard
      title="Остатки сейчас" loading={load.loading} error={load.error ? load.error.text : null} onRetry={load.retry}
      subtitle={load.data ? `${fmtPack(total)} уп. в ${fmtInt(rows.length)} ${plural(rows.length, 'аптеке', 'аптеках', 'аптеках')} зоны · срок ≤ 90 дн. - в ${fmtInt(expiring)}. Текущие остатки, не за период.` : 'Текущие остатки, не за период'}
    >
      {!load.data && !load.error && <ChartSkeleton height={200} />}
      {load.data && !rows.length && <NoData height={120} text="Товара нет в остатках" />}
      {rows.length > 0 && (
        <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, maxHeight: 420 }}>
          <Table size="small" stickyHeader sx={TABLE_SX}>
            <TableHead>
              <TableRow>
                <TableCell>Аптека</TableCell>
                <TableCell align="right">Остаток, уп.</TableCell>
                <TableCell align="right">Ближайший срок</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(0, shown).map(({ stock, pharmacy }) => {
                const nearest = stock.expiry && stock.expiry.nearest;
                const expired = stock.expiry ? stock.expiry.expiredQty : 0;
                return (
                  <TableRow key={stock.guid}>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }} noWrap title={pharmacy ? pharmacy.name : stock.guid}>{pharmacy ? pharmacy.name : stock.guid}</Typography>
                      <SubText>{[pharmacy && pharmacy.kind === 'warehouse' ? 'склад' : null, pharmacy && pharmacy.region].filter(Boolean).join(' · ') || '-'}</SubText>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 120 }}><ShareBar value={stock.qty} max={max} label={fmtPack(stock.qty)} /></TableCell>
                    <TableCell align="right" sx={{ color: nearest && nearest.daysLeft <= 90 ? `${WATCH_COLORS.warn} !important` : undefined }}>
                      {nearest ? fmtDay(nearest.date) : '-'}
                      <SubText color={expired ? GROUP_COLORS.error : undefined}>
                        {expired ? `просрочено ${fmtPack(expired)} уп.` : nearest ? `через ${fmtInt(nearest.daysLeft)} дн., ${fmtPack(nearest.qty)} уп.` : 'нет данных'}
                      </SubText>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {rows.length > shown && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Button size="small" onClick={() => setShown(s => s + PAGE)} sx={{ textTransform: 'none' }}>
            Показать ещё {Math.min(PAGE, rows.length - shown)} из {fmtInt(rows.length - shown)}
          </Button>
        </Box>
      )}
      {load.data && load.data.truncated && <SubText>Список остатков сокращён сервером.</SubText>}
    </ChartCard>
  );
};

const Body: React.FC<{
  data: ProductCardResponse; period: Period; reloadToken: number; onOpen: (id: string) => void;
  cohort: (extra: Partial<DocumentFilters>, range?: Period) => void;
}> = ({ data, period, reloadToken, onOpen, cohort }) => {
  const [shownPh, setShownPh] = useState(PAGE);
  const stats = useMemo(() => summaryStats(data), [data]);
  const by = data.by;
  const { concentration, pingPong, resend } = data.signals;
  const maxFlow = data.flows.length ? data.flows[0].qty : 0;
  const hasTimeline = data.timeline.some(p => p.docs);
  const openBar = (kind: Kind) => (item: { payload?: unknown }) => {
    const p = item.payload as Point | undefined;
    if (p && p[kind]) cohort(kindPatch(kind), bucketRange(p.date, by, period));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <StatStrip items={stats} />
      <ChartCard wide title="Динамика упаковок" subtitle="Упаковки по видам документов, созданных в периоде. Бледный столбец - неделя, которая входит в период частично.">
        {!hasTimeline ? <NoData height={HEIGHT - 80} text="В периоде товар не двигался" /> : (
          <>
            <SeriesLegend items={LEGEND} hidden={[]} onToggle={() => undefined} />
            <Box sx={{ height: HEIGHT - 60 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.timeline} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
                  <CartesianGrid stroke={CHART.grid} vertical={false} />
                  <XAxis dataKey="date" tick={AXIS_TICK} tickFormatter={d => dm(String(d))} axisLine={{ stroke: BORDER }} tickLine={false} minTickGap={14} />
                  <YAxis tick={AXIS_TICK} tickFormatter={v => fmtPack(Number(v))} axisLine={false} tickLine={false} width={52} />
                  <ChartTooltip content={<Tip by={by} />} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
                  {KINDS.map(k => (
                    <Bar key={k} dataKey={k} stackId="k" fill={KIND_COLORS[k]} isAnimationActive={false} maxBarSize={40} onClick={openBar(k)} style={{ cursor: 'pointer' }}>
                      {data.timeline.map(p => <Cell key={p.date} fillOpacity={p.partial ? 0.55 : 1} />)}
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </>
        )}
      </ChartCard>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, gap: 1.5 }}>
        <ChartCard
          title="Потоки" subtitle={`Откуда и куда двигался товар${data.flowsTotal > data.flows.length ? `: ${fmtInt(data.flows.length)} крупнейших из ${fmtInt(data.flowsTotal)}` : ''}. Клик - документы потока.`}
        >
          {!data.flows.length ? <NoData height={120} text="Нет перемещений" /> : (
            <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5 }}>
              <Table size="small" sx={TABLE_SX}>
                <TableHead>
                  <TableRow>
                    <TableCell>Откуда → куда</TableCell>
                    <TableCell align="right">Документов</TableCell>
                    <TableCell align="right">Упаковок</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.flows.map(f => (
                    <TableRow key={`${f.src}|${f.dst}`} hover sx={{ cursor: 'pointer' }} onClick={() => cohort({ sender: [f.src], recipient: [f.dst] })}>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography sx={{ fontSize: '0.78rem' }} noWrap title={`${f.srcName} → ${f.dstName}`}>{f.srcName} → {f.dstName}</Typography>
                        {f.returnQty > 0 && <SubText color={KIND_COLORS.return}>возвратом {fmtPack(f.returnQty)} уп.</SubText>}
                      </TableCell>
                      <TableCell align="right">{fmtInt(f.docs)}</TableCell>
                      <TableCell align="right" sx={{ minWidth: 120 }}><ShareBar value={f.qty} max={maxFlow} label={fmtPack(f.qty)} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </ChartCard>
        <ChartCard title="Сигналы" subtitle="Признаки лишних или неудачных перемещений товара в периоде">
          <SignalBlock
            title={`Концентрация: ${fmtPct(concentration.share)}`} color={concentration.share !== null && concentration.share >= 0.8 && concentration.senders > 3 ? WATCH_COLORS.warn : undefined}
            text={`Доля упаковок от 3 крупнейших отправителей из ${fmtInt(concentration.senders)}`}
          >
            {concentration.top.map(s => <SignalRow key={s.guid} name={s.name} value={`${fmtPack(s.qty)} уп. · ${fmtPct(s.share)}`} onClick={() => cohort({ sender: [s.guid] })} />)}
          </SignalBlock>
          <SignalBlock
            title={`Пинг-понг: ${fmtInt(pingPong.count)}`} color={pingPong.count ? WATCH_COLORS.warn : undefined}
            text="Товар ушёл из A в B и в течение 14 дней вернулся из B в A перемещением (возвраты не считаются)"
          >
            {pingPong.pairs.map(p => (
              <SignalRow key={`${p.a}|${p.b}`} name={`${p.aName} ⇄ ${p.bName}`} value={`${fmtInt(p.count)} раз · ${fmtPack(p.qty)} уп.`} onClick={() => cohort({ pharmacy: [p.a, p.b] })} />
            ))}
          </SignalBlock>
          <SignalBlock
            title={`Перепересылка: ${fmtInt(resend.count)}`} color={resend.count ? WATCH_COLORS.warn : undefined}
            text="Аптека получила товар и в течение 7 дней сама отправила его дальше (не возвратом)"
          >
            {resend.pharmacies.map(p => (
              <SignalRow key={p.guid} name={p.name} value={`${fmtInt(p.count)} раз · ${fmtPack(p.qty)} уп.`} onClick={() => cohort({ pharmacy: [p.guid] })} />
            ))}
          </SignalBlock>
        </ChartCard>
      </Box>

      <ChartCard wide title="Аптеки и склады" subtitle={`${fmtInt(data.pharmacies.length)} в вашей зоне: сколько отправили, получили и вернули упаковок. Клик - документы с товаром по аптеке.`}>
        {!data.pharmacies.length ? <NoData height={120} text="Нет аптек в зоне" /> : (
          <>
            <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, maxHeight: 520 }}>
              <Table size="small" stickyHeader sx={TABLE_SX}>
                <TableHead>
                  <TableRow>
                    <TableCell>Аптека</TableCell>
                    <TableCell align="right">Отправила</TableCell>
                    <TableCell align="right">Получила</TableCell>
                    <TableCell align="right">Из отправленного возвратом</TableCell>
                    <TableCell align="right">Нехватка остатка</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.pharmacies.slice(0, shownPh).map(p => (
                    <TableRow key={p.guid} hover sx={{ cursor: 'pointer' }} onClick={() => cohort({ pharmacy: [p.guid] })}>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }} noWrap title={p.name}>{p.name}</Typography>
                        <SubText>{[p.kind === 'warehouse' ? 'склад' : null, p.region].filter(Boolean).join(' · ') || '-'}</SubText>
                      </TableCell>
                      <TableCell align="right">{p.sent ? fmtPack(p.sent) : '-'}<SubText>{p.sentDocs ? `${fmtInt(p.sentDocs)} док.` : ''}</SubText></TableCell>
                      <TableCell align="right">{p.received ? fmtPack(p.received) : '-'}<SubText>{p.receivedDocs ? `${fmtInt(p.receivedDocs)} док.` : ''}</SubText></TableCell>
                      <TableCell align="right" sx={{ color: p.returned ? `${KIND_COLORS.return} !important` : undefined }}>{p.returned ? fmtPack(p.returned) : '-'}</TableCell>
                      <TableCell align="right" sx={{ color: p.shortLines ? `${WATCH_COLORS.warn} !important` : undefined }}>
                        {p.shortLines ? `${fmtInt(p.shortLines)} стр.` : '-'}
                        {p.shortLines > 0 && <SubText>{fmtPack(p.shortQty)} уп.</SubText>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {data.pharmacies.length > shownPh && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <Button size="small" onClick={() => setShownPh(s => s + PAGE)} sx={{ textTransform: 'none' }}>
                  Показать ещё {Math.min(PAGE, data.pharmacies.length - shownPh)} из {fmtInt(data.pharmacies.length - shownPh)}
                </Button>
              </Box>
            )}
          </>
        )}
      </ChartCard>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: data.remains.allowed && data.remains.sku ? '1fr 1fr' : '1fr' }, gap: 1.5 }}>
        {data.remains.allowed && data.remains.sku && <RemainsCard id={data.product.id} sku={data.remains.sku} reloadToken={reloadToken} />}
        <ChartCard
          title="Аналоги по МНН"
          subtitle={data.analogs.mnn ? `${data.analogs.mnn}: ${fmtInt(data.analogs.total)} ${plural(data.analogs.total, 'товар', 'товара', 'товаров')} в справочнике, упаковки за период. Клик - карточка аналога.` : 'МНН в справочнике не указано'}
        >
          {!data.analogs.items.length ? <NoData height={120} text={data.analogs.mnn ? 'Других товаров с этим МНН нет' : 'Аналоги подбираются по МНН'} /> : (
            <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 420, overflowY: 'auto' }}>
              {data.analogs.items.map(a => (
                <Box
                  key={a.id} component="button" type="button" onClick={() => onOpen(a.id)}
                  sx={{
                    display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 130px', gap: 1, alignItems: 'center', px: 0.75, py: 0.6, border: 'none',
                    borderBottom: '1px solid #f1f5f9', bgcolor: 'transparent', font: 'inherit', textAlign: 'left', cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' }
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }} noWrap title={a.name}>{a.name}</Typography>
                    <SubText>{[a.producer, a.rxotc, a.docs ? `${fmtInt(a.docs)} док.` : 'не двигался'].filter(Boolean).join(' · ')}</SubText>
                  </Box>
                  <ShareBar value={a.qty} max={data.analogs.items[0].qty} label={fmtPack(a.qty)} />
                </Box>
              ))}
            </Box>
          )}
        </ChartCard>
      </Box>
    </Box>
  );
};

/** Карточка товара: показатели к прошлому периоду, динамика, потоки, сигналы, аптеки, остатки и аналоги */
const ProductCard: React.FC<Props> = ({ id, params, paramsKey, period, reloadToken, onClose, onOpen, onCohort }) => {
  const [by, setByState] = useBucketBy(diffDays(period.from, period.to) + 1);
  const [byTouched, setByTouched] = useState(false);
  const setBy = (v: BucketBy) => { setByTouched(true); setByState(v); };
  // открытие карточки пишется в журнал один раз; смена «по дням / по неделям» - без записи
  const load = useLoad(
    (silent: boolean, signal: AbortSignal) => analyticsApi.product(id, params, by, silent || byTouched, signal),
    `product|${id}|${by}|${paramsKey}`, reloadToken, 'Не удалось загрузить карточку товара'
  );
  const data = load.data;
  const product = data ? data.product : null;
  const option: ProductOption | null = product
    ? { id: product.id, name: product.name, code: product.sku || '', barcode: '', docs: data ? data.summary.cur.docs : 0 }
    : null;
  const cohort = (extra: Partial<DocumentFilters>, range?: Period) => { if (option) onCohort({ ...extra, products: [option] }, range); };
  const meta = product ? [product.sku ? `арт. ${product.sku}` : null, product.producer, product.mnn ? `МНН: ${product.mnn}` : null, product.type, product.rxotc, product.folder].filter(Boolean) : [];

  return (
    <Dialog open fullWidth maxWidth="lg" onClose={onClose} PaperProps={{ sx: { bgcolor: '#f8fafc' } }}>
      <DialogTitle sx={{ bgcolor: '#fff', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.5 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>{product ? product.name : 'Карточка товара'}</Typography>
          {meta.length > 0 && <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>{meta.join(' · ')}</Typography>}
          {product && (product.notForMovements || !product.inCatalog) && (
            <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5 }}>
              {product.notForMovements && <Chip size="small" label="Запрет перемещения в 1С" sx={{ bgcolor: '#fef2f2', color: GROUP_COLORS.error, fontWeight: 600 }} />}
              {!product.inCatalog && !data!.catalogError && <Chip size="small" label="Нет в справочнике 1С" sx={{ bgcolor: '#fffbeb', color: WATCH_COLORS.warn, fontWeight: 600 }} />}
            </Box>
          )}
        </Box>
        <ByToggle value={by} onChange={setBy} />
        {option && (
          <Button size="small" variant="outlined" onClick={() => cohort({})} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>Документы с товаром</Button>
        )}
        <IconButton size="small" onClick={onClose} aria-label="Закрыть"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important', opacity: load.loading && data ? 0.65 : 1, transition: 'opacity 0.15s' }}>
        {load.error && (
          <Alert severity="error" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={load.retry}>Повторить</Button>}>
            {load.error.text}{data ? '. На экране - данные прежнего запроса.' : ''}
          </Alert>
        )}
        {data && data.catalogError && <Alert severity="warning" sx={{ mb: 1.5 }}>Справочник 1С недоступен: признаки и аналоги товара не показаны.</Alert>}
        {!data && !load.error && <ChartSkeleton height={520} />}
        {data && <Body data={data} period={period} reloadToken={reloadToken} onOpen={onOpen} cohort={cohort} />}
      </DialogContent>
    </Dialog>
  );
};

export default ProductCard;
